import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { AlarmClock } from "lucide-react";

/**
 * Prompts the operator for a reason when marking a receipt as URGENT.
 * `open` controls visibility. `row` is the target receipt. On submit calls
 * `onConfirm(row, reason)`. Cancelling calls `onOpenChange(false)`.
 */
export default function UrgentReasonDialog({ open, onOpenChange, row, onConfirm }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setReason(row?.urgent_reason || ""); }
  }, [open, row]);

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = reason.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      await onConfirm(row, trimmed);
      onOpenChange(false);
    } finally { setSaving(false); }
  };

  const presets = [
    "Customer travelling — needs before departure",
    "Medical / hospital admission",
    "Government interview / verification scheduled",
    "Document deadline expiring soon",
    "VIP / referral customer",
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-white w-[calc(100vw-2rem)] sm:w-full" data-testid="urgent-reason-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl tracking-tight flex items-center gap-2 text-red-700">
            <AlarmClock className="h-5 w-5" />
            Mark as URGENT
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="text-sm text-slate-600 bg-red-50 border border-red-100 rounded-md px-3 py-2">
            <span className="font-semibold">{row?.customer_name || "—"}</span>
            {row?.doc_no && <span className="text-slate-500"> · {row.doc_no}</span>}
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Reason (required)</Label>
            <Textarea
              autoFocus
              required
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Customer is travelling on 30 Aug and needs the certificate before then…"
              data-testid="urgent-reason-input"
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {presets.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setReason(p)}
                className="text-[11px] px-2 py-1 rounded-full bg-slate-100 hover:bg-red-100 hover:text-red-700 text-slate-600"
              >
                {p}
              </button>
            ))}
          </div>
          <DialogFooter className="pt-1 flex-col-reverse sm:flex-row gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
            <Button
              type="submit"
              disabled={saving || !reason.trim()}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="urgent-reason-save"
            >
              {saving ? "Marking…" : "Mark URGENT"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
